/** Review one Client PO: document left, fields + project assignment right. */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Sparkles, RefreshCw, Trash2, Check, Lock, Undo2, Maximize2, Minimize2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { InvoiceViewer } from '../cost-inbox/InvoiceViewer';
import { ProjectPicker } from '../cost-inbox/ProjectPicker';

const FIELDS = [
  ['client_name', 'Client'], ['po_number', 'PO number'], ['po_date', 'PO date', 'date'], ['value_pounds', 'Value ex VAT (£)', 'number'],
  ['client_ref', 'Client ref'], ['period', 'Period'], ['site_name', 'Site / contact'], ['site_address', 'Site address'], ['site_postcode', 'Postcode'],
];
const STATUS_LABEL = { new: 'New', assigned: 'Assigned', closed: 'Closed' };

function Field({ id, label, type = 'text', value, onChange, disabled, testid }) {
  return (
    <div>
      <Label htmlFor={id} className="text-[11px] uppercase tracking-wider text-gray-400">{label}</Label>
      <Input id={id} type={type} step={type === 'number' ? '0.01' : undefined} value={value ?? ''} onChange={e => onChange(e.target.value)} disabled={disabled}
        className="mt-1 bg-[#1E2530] border-white/15 text-white text-sm h-9" data-testid={testid} />
    </div>
  );
}

export function ClientPoDialog({ api, poId, onClose, onChanged }) {
  const [po, setPo] = useState(null);
  const [form, setForm] = useState({});
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [docExpanded, setDocExpanded] = useState(false);

  const apply = useCallback((p) => {
    setPo(p);
    setForm({ client_name: p.client_name, po_number: p.po_number, po_date: p.po_date, value_pounds: p.value_pounds, client_ref: p.client_ref, period: p.period,
      site_name: p.site_name, site_address: p.site_address, site_postcode: p.site_postcode, description: p.description, notes: p.notes, project_id: p.project_id, reverse_charge: !!p.reverse_charge });
    setDirty(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get(`/client-pos/${poId}`).then(r => { if (!cancelled) apply(r.data); }).catch(() => toast.error('Could not load the PO'));
    api.get('/projects?limit=200').then(r => { if (!cancelled) setProjects(r.data || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [api, poId, apply]);
  useEffect(() => {
    if (!po || (po.extraction_status !== 'pending' && po.extraction_status !== 'running')) return undefined;
    const t = setInterval(() => api.get(`/client-pos/${poId}`).then(r => { if (!dirty) apply(r.data); else setPo(r.data); }).catch(() => {}), 2500);
    return () => clearInterval(t);
  }, [api, poId, po, dirty, apply]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };
  const locked = po?.status === 'closed';

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const r = await api.patch(`/client-pos/${poId}`, { ...form, ...extra });
      const assigning = (extra.project_id || form.project_id) && po?.status === 'new';
      apply(r.data);
      toast.success(extra.status === 'closed' ? 'PO closed' : extra.status === 'assigned' && po?.status === 'closed' ? 'PO re-opened' : assigning ? `PO assigned to ${r.data.project_ref}` : 'Saved');
      if (r.data.duplicate_of) {
        const d = r.data.duplicate_of;
        toast.warning(`PO ${r.data.po_number} already exists for ${d.client_name || 'this client'}${d.project_ref ? ` on ${d.project_ref}` : ' (not yet assigned)'} — check it isn't a duplicate`, { duration: 9000 });
      }
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };
  const acceptSuggestion = () => { set('project_id', po.suggestion.project_id); save({ project_id: po.suggestion.project_id }); };
  const dismissSuggestion = async () => {
    setBusy(true);
    try { const r = await api.post(`/client-pos/${poId}/suggestion/dismiss`); apply(r.data); toast.success('Suggestion dismissed — pick the project when it’s confirmed'); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not dismiss'); }
    finally { setBusy(false); }
  };
  const restoreSuggestion = async () => {
    setBusy(true);
    try { const r = await api.post(`/client-pos/${poId}/suggestion/restore`); apply(r.data); toast.success('Suggestion restored'); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not restore'); }
    finally { setBusy(false); }
  };
  const reread = async () => {
    setBusy(true);
    try { const r = await api.post(`/client-pos/${poId}/reread`); apply(r.data); toast.success('Re-read'); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Re-read failed'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this PO? It will disappear from the ledger and the project.')) return;
    try { await api.delete(`/client-pos/${poId}`); toast.success('PO deleted'); onChanged?.(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };

  const reading = po && (po.extraction_status === 'pending' || po.extraction_status === 'running');
  const needsAssign = !!form.project_id && po?.status === 'new';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[96vw] w-[1500px] h-[92vh] p-0 bg-[#2A3240] border-white/10 text-white flex flex-col" data-testid="client-po-dialog">
        <DialogHeader className="px-4 py-3 border-b border-white/10">
          <DialogTitle className="text-base flex items-center gap-2 flex-wrap">
            Client PO {po?.po_number ? `· ${po.po_number}` : ''}
            {po && <span className={`text-[10px] px-2 py-0.5 rounded-full border ${po.status === 'assigned' ? 'border-emerald-400/40 text-emerald-200' : po.status === 'closed' ? 'border-white/20 text-gray-300' : 'border-[#F5A623]/50 text-[#F5A623]'}`} data-testid="client-po-status">{STATUS_LABEL[po.status]}</span>}
            {po?.email_in && <span className="text-xs text-sky-200/90 font-normal">· emailed by {po.email_in.from_name || po.email_in.from}{po.email_in.subject ? ` — "${po.email_in.subject}"` : ''}</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 flex flex-col lg:grid lg:grid-cols-[1.1fr_1fr]">
          <div className={`relative min-h-0 shrink-0 lg:shrink lg:h-auto lg:flex-none border-b lg:border-b-0 lg:border-r border-white/10 bg-[#1E2530] ${docExpanded ? 'flex-1' : 'h-[40vh]'}`} data-testid="client-po-document-pane">
            {po && <InvoiceViewer api={api} itemId={po.id} pageCount={po.page_count || 1} filename={po.original_filename || 'po'} base="/client-pos" />}
            <button type="button" onClick={() => setDocExpanded(x => !x)}
              className="lg:hidden absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-[#F5A623] text-[#1E2530] text-xs font-semibold px-3 py-2 shadow-lg active:scale-95 transition-transform"
              data-testid="client-po-doc-expand">
              {docExpanded ? <><Minimize2 className="w-3.5 h-3.5" /> Show details</> : <><Maximize2 className="w-3.5 h-3.5" /> Expand PO</>}
            </button>
          </div>
          <div className={`min-h-0 flex-1 overflow-y-auto p-4 space-y-4 ${docExpanded ? 'hidden lg:block' : ''}`} data-testid="client-po-details-pane">
            {reading && <div className="text-xs text-[#F5A623] inline-flex items-center gap-2" data-testid="client-po-reading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> AI is reading the PO…</div>}
            {po?.extraction_status === 'failed' && <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-400/30 rounded px-2.5 py-1.5" data-testid="client-po-read-failed">AI read failed — {po.extraction_error || 'fill the fields by hand'}</div>}

            <div className="bg-[#1E2530]/60 border border-white/10 rounded-lg p-3" data-testid="client-po-project-block">
              <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-1.5">Project</div>
              <ProjectPicker value={form.project_id} onChange={(id) => set('project_id', id)} projects={projects} disabled={locked} testid="client-po-project-picker" company="CON" api={api} />
              {po?.status === 'new' && form.project_id && po.suggestion?.project_id === form.project_id && (
                <div className="mt-2 flex items-center justify-between gap-2 text-xs text-[#F5A623]" data-testid="client-po-prefilled">
                  <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Pre-filled: {po.suggestion.reason} — press <b>Save &amp; assign</b> to confirm</span>
                  <Button size="sm" variant="ghost" onClick={dismissSuggestion} disabled={busy || locked} className="h-7 text-gray-300 hover:text-white hover:bg-white/10 shrink-0" title="Not this project — clear the suggestion" data-testid="client-po-dismiss-prefilled"><X className="w-3.5 h-3.5 mr-1" /> Not this one</Button>
                </div>
              )}
              {!form.project_id && po?.suggestion && (
                <div className="mt-2 flex items-center justify-between gap-2 text-xs bg-[#F5A623]/10 border border-[#F5A623]/30 rounded px-2.5 py-1.5" data-testid="client-po-suggestion">
                  <span className="inline-flex items-center gap-1.5 text-[#F5A623]"><Sparkles className="w-3.5 h-3.5" /> Suggested: <b>{po.suggestion.project_ref}</b> <span className="text-gray-400">({po.suggestion.confidence} — {po.suggestion.reason})</span></span>
                  <span className="flex items-center gap-1 shrink-0">
                    <Button size="sm" onClick={acceptSuggestion} disabled={busy || locked} className="h-7 bg-[#F5A623] text-[#1E2530] hover:bg-[#f7b84a]" data-testid="client-po-accept-suggestion"><Check className="w-3.5 h-3.5 mr-1" /> Use</Button>
                    <Button size="sm" variant="ghost" onClick={dismissSuggestion} disabled={busy || locked} className="h-7 text-gray-300 hover:text-white hover:bg-white/10" title="Not this project — hide the suggestion" data-testid="client-po-dismiss-suggestion"><X className="w-3.5 h-3.5 mr-1" /> Not this one</Button>
                  </span>
                </div>
              )}
              {!po?.suggestion && po?.suggestion_dismissed && (
                <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-gray-400" data-testid="client-po-suggestion-dismissed">
                  <span>Suggestion <b className="text-gray-300">{po.suggestion_dismissed.project_ref}</b> dismissed{po.suggestion_dismissed.by_name ? ` by ${po.suggestion_dismissed.by_name}` : ''} — waiting on confirmation</span>
                  {!locked && <button type="button" onClick={restoreSuggestion} disabled={busy} className="inline-flex items-center gap-1 text-gray-300 hover:text-white" data-testid="client-po-restore-suggestion"><Undo2 className="w-3 h-3" /> Restore</button>}
                </div>
              )}
              {po?.project_ref && form.project_id === po.project_id && <div className="mt-1.5 text-[11px] text-emerald-300">Assigned{po.assigned_by_name ? ` by ${po.assigned_by_name}` : ''}{po.assigned_at ? ` · ${new Date(po.assigned_at).toLocaleDateString('en-GB')}` : ''}</div>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {FIELDS.map(([k, label, type]) => (
                <div key={k} className={k === 'site_address' ? 'col-span-2' : ''}>
                  <Field id={`po-${k}`} label={label} type={type} value={form[k]} onChange={(v) => set(k, v)} disabled={locked} testid={`client-po-field-${k}`} />
                </div>
              ))}
              <label className="col-span-2 inline-flex items-center gap-2 text-xs text-gray-300">
                <input type="checkbox" checked={!!form.reverse_charge} onChange={e => set('reverse_charge', e.target.checked)} disabled={locked} className="accent-[#F5A623]" data-testid="client-po-reverse-charge" /> Reverse charge VAT applies
              </label>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-gray-400">Works ordered</Label>
              <Input value={form.description ?? ''} onChange={e => set('description', e.target.value)} disabled={locked} className="mt-1 bg-[#1E2530] border-white/15 text-white text-sm h-9" data-testid="client-po-field-description" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-gray-400">Notes</Label>
              <Textarea value={form.notes ?? ''} onChange={e => set('notes', e.target.value)} disabled={locked} rows={2} className="mt-1 bg-[#1E2530] border-white/15 text-white text-sm" data-testid="client-po-field-notes" />
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 px-4 py-3 flex items-center gap-2 flex-wrap">
          {!locked && <Button variant="ghost" size="sm" onClick={reread} disabled={busy || reading} className="text-gray-300 hover:text-white hover:bg-white/10" data-testid="client-po-reread"><RefreshCw className="w-4 h-4 mr-1.5" /> Re-read</Button>}
          <Button variant="ghost" size="sm" onClick={remove} disabled={busy} className="text-red-300 hover:text-red-200 hover:bg-red-500/10" data-testid="client-po-delete"><Trash2 className="w-4 h-4 mr-1.5" /> Delete</Button>
          <div className="flex-1" />
          {locked ? (
            <Button variant="outline" onClick={() => save({ status: 'assigned' })} disabled={busy} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="client-po-reopen"><Undo2 className="w-4 h-4 mr-1.5" /> Re-open</Button>
          ) : (
            <>
              {po?.status === 'assigned' && <Button variant="outline" onClick={() => save({ status: 'closed' })} disabled={busy || dirty} title={dirty ? 'Save your changes first' : 'Mark as fully invoiced / complete'} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="client-po-close"><Lock className="w-4 h-4 mr-1.5" /> Close PO</Button>}
              <Button onClick={() => save()} disabled={busy || !(dirty || needsAssign)} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#f7b84a]" data-testid="client-po-save">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />} {needsAssign ? 'Save & assign' : 'Save'}
              </Button>
            </>
          )}
          <Button variant="ghost" onClick={onClose} className="text-gray-300 hover:text-white hover:bg-white/10" data-testid="client-po-close-dialog">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
